import React, { useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import Logo from './Logo';
import './Hero.css';

const SHOW_DEVI_GALLERY = true;

const DEITY_IMAGE_BY_NAME = {
  'Maa Durga': '/images/deities/durga.webp',
  'Goddess Saraswati': '/images/deities/saraswati.webp',
  'Maa Parvati': '/images/deities/parvati.webp',
  'Maa Kali': '/images/deities/kali.webp',
  'Maa Lakshmi': '/images/deities/lakshmi.webp',
  'Lord Ganesha': '/images/deities/ganesha.webp',
  'Lord Hanuman': '/images/deities/hanuman.webp',
  'Lord Shiva': '/images/deities/shiva.webp',
  'Lord Vishnu': '/images/deities/vishnu.webp',
  'Lord Murugan': '/images/deities/guru.webp',
  'Lord Brahma': '/images/deities/navgraha.webp',
};

const DEVI_CAROUSEL_ITEMS = [
  {
    name: 'Maa Durga',
    imageUrl: '/images/deities/durga.jpg',
  },
  {
    name: 'Goddess Saraswati',
    imageUrl: '/images/deities/saraswati.jpg',
  },
  {
    name: 'Maa Parvati',
    imageUrl: '/images/deities/parvati.jpg',
  },
  {
    name: 'Maa Kali',
    imageUrl: '/images/deities/kali.jpg',
  },
  {
    name: 'Maa Lakshmi',
    imageUrl: '/images/deities/lakshmi.jpg',
  },
  {
    name: 'Lord Ganesha',
    imageUrl: '/images/deities/ganesha.jpg',
  },
  {
    name: 'Lord Hanuman',
    imageUrl: '/images/deities/hanuman.jpg',
  },
  {
    name: 'Lord Shiva',
    imageUrl: '/images/deities/shiva.jpg',
  },
  {
    name: 'Lord Vishnu',
    imageUrl: '/images/deities/vishnu.jpg',
  },
  {
    name: 'Lord Murugan',
    imageUrl: '/images/deities/murugan.jpg',
  },
  {
    name: 'Lord Brahma',
    imageUrl: '/images/deities/brahma.jpg',
  },
  {
    name: 'Maa Bagalamukhi',
    imageUrl: '/images/deities/bagalamukhi.jpg',
  },
  {
    name: 'Lord Kubera',
    imageUrl: '/images/deities/kubera.jpg',
  },
  {
    name: 'Lord Shani',
    imageUrl: '/images/deities/shani.jpg',
  },
  {
    name: 'Navgraha',
    imageUrl: '/images/deities/navgraha.jpg',
  },
  {
    name: 'Vastu Purusha',
    imageUrl: '/images/deities/vastu_purusha.jpg',
  },
  {
    name: 'Spiritual Guru',
    imageUrl: '/images/deities/guru.jpg',
  }
];

const DEITY_LABEL_KEYS = {
  'Maa Durga': 'home.hero.deity.maaDurga',
  'Goddess Saraswati': 'home.hero.deity.goddessSaraswati',
  'Maa Parvati': 'home.hero.deity.maaParvati',
  'Maa Kali': 'home.hero.deity.maaKali',
  'Maa Lakshmi': 'home.hero.deity.maaLakshmi',
  'Lord Ganesha': 'home.hero.deity.lordGanesha',
  'Lord Hanuman': 'home.hero.deity.lordHanuman',
  'Lord Shiva': 'home.hero.deity.lordShiva',
  'Lord Vishnu': 'home.hero.deity.lordVishnu',
  'Lord Krishna': 'home.hero.deity.lordKrishna',
  'Lord Murugan': 'home.hero.deity.lordMurugan',
  'Lord Brahma': 'home.hero.deity.lordBrahma',
  'Maa Bagalamukhi': 'home.hero.deity.maaBagalamukhi',
  'Lord Kubera': 'home.hero.deity.lordKubera',
  'Lord Shani': 'home.hero.deity.lordShani',
  'Navgraha': 'home.hero.deity.navgraha',
  'Vastu Purusha': 'home.hero.deity.vastuPurusha',
  'Spiritual Guru': 'home.hero.deity.guru',
  // repeating to have a continuous carosel 
  'Maa Lakshmi': 'home.hero.deity.maaLakshmi',
  'Lord Ganesha': 'home.hero.deity.lordGanesha',
  'Lord Hanuman': 'home.hero.deity.lordHanuman',
};

const Hero = ({ onServiceClick }) => {
  const [activeCard, setActiveCard] = useState(0);
  const prefersReducedMotion = useReducedMotion();
  const { t } = useLanguage();

  const getDeityLabel = (name) => {
    const key = DEITY_LABEL_KEYS[name];
    if (!key) return name;
    return t(key, {}, name);
  };

  const serviceOptions = [
    {
      id: 'bhakti-samadhan',
      icon: 'fas fa-om',
      title: t('home.hero.cards.bhaktiSamadhan.title'),
      description: t('home.hero.cards.bhaktiSamadhan.description'),
      imageUrl: '/images/pages/home/carosel/bs.jpg'
    },
    {
      id: 'online-pooja',
      icon: 'fas fa-hands-praying',
      title: t('home.hero.cards.onlinePooja.title'),
      description: t('home.hero.cards.onlinePooja.description'),
      imageUrl: '/images/pages/home/carosel/op.jpg'
    },
    {
      id: 'live-darshan',
      icon: 'fas fa-eye',
      title: t('home.hero.cards.bhaktiDarshan.title'),
      description: t('home.hero.cards.bhaktiDarshan.description'),
      imageUrl: '/images/pages/home/carosel/bd.jpg'
    },
    {
      id: 'bhakti-store',
      icon: 'fas fa-store',
      title: t('home.hero.cards.bhaktiStore.title'),
      description: t('home.hero.cards.bhaktiStore.description'),
      imageUrl: '/images/pages/home/carosel/bstore.jpg'
    },
    {
      id: 'bhakti-yoga',
      icon: 'fas fa-spa',
      title: t('home.hero.cards.bhaktiYoga.title'),
      description: t('home.hero.cards.bhaktiYoga.description'),
      imageUrl: '/images/pages/home/carosel/by.jpg'
    },
    {
      id: 'bhakti-jaapa',
      icon: 'fas fa-book-open',
      title: t('home.hero.cards.bhaktiJaapa.title'),
      description: t('home.hero.cards.bhaktiJaapa.description'),
      imageUrl: '/images/pages/home/carosel/bj.jpg'
    },
  ];
  const marqueeServices = [...serviceOptions, ...serviceOptions];
  const carouselServices = [...DEVI_CAROUSEL_ITEMS, ...DEVI_CAROUSEL_ITEMS];

  const renderCardTitle = (title) => {
    const lines = title.split('\n');

    if (lines.length === 1) {
      return title;
    }

    return (
      <>
        {lines.map((line) => (
          <span key={line} className="card-title-line">
            {line}
          </span>
        ))}
      </>
    );
  };

  return (
    <section className="hero">
      {/* Bhakti Darshan Widget - Restored Style with Framer Motion */}
      <motion.div
        className="live-darshan-widget-wrap"
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, delay: 1, type: "spring" }}
      >
        <div
          className="live-darshan-widget"
          onClick={() => onServiceClick('live-darshan')}
        >
          <span className="watch-live-text">{t('home.hero.liveDarshan')}</span>
          <div className='watch-live-butt'>{t('home.hero.watchNow')}</div>
        </div>
        <button
          type="button"
          className="bhakti-chitrakatha-btn"
          onClick={() => onServiceClick('bhakti-bal-chitra-katha')}
        >
          <span className="bhakti-chitrakatha-line">{t('home.hero.bhakti')}</span>
          <span className="bhakti-chitrakatha-line">{t('home.hero.balChitrakatha')}</span>
        </button>
      </motion.div>

      {/* Top Content */}
      <div className="hero-content-top">
        <motion.div
          className="hero-logo"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1 }}
        >
          <Logo size="xlarge" />
        </motion.div>

        <motion.h1
          className="hero-title"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <span className="highlight">Anandmaya</span>
        </motion.h1>

        <motion.p
          className="hero-title-subtext"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <span className="hero-title-subtext">( a brand of MAHAV TECHNOLOGIES LLP )</span>

        </motion.p>

        <motion.p
          className="hero-subtitle1"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          {t('home.hero.mindBodySoul')}
        </motion.p>

        <motion.p
          className="hero-subtitle"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          {t('home.hero.subtitle')}
        </motion.p>

      </div>

      {SHOW_DEVI_GALLERY && (
        <div className="devi-gallery" aria-label={t('home.hero.deviGalleryAria')}>
          <div className="devi-carousel-shell">
            <motion.div
              className="devi-carousel-track"
              animate={prefersReducedMotion ? { x: 0 } : { x: ['0%', '-50%'] }}
              transition={
                prefersReducedMotion
                  ? { duration: 0 }
                  : { duration: 30, repeat: Infinity, ease: 'linear' }
              }
            >
              {carouselServices.map((devi, index) => (
                <motion.div
                  key={`${devi.name}-${index}`}
                  className="devi-carousel-card"
                  initial={{ opacity: 0.98 }}
                  whileHover={{ y: -4, scale: 1.02 }}
                  transition={{ type: 'spring', stiffness: 220, damping: 24 }}
                >
                  <img
                    src={devi.imageUrl}
                    alt={getDeityLabel(devi.name)}
                    className="devi-carousel-image"
                    width="140"
                    height="168"
                    loading="lazy"
                    decoding="async"
                    fetchpriority="low"
                  />
                  <div className="devi-carousel-overlay">
                    <span className="devi-carousel-name">{getDeityLabel(devi.name)}</span>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      )}

      {/* The Expansion Deck */}
      <div className="hero-deck-container">
        <div className="deck-grid">
          <motion.div
            className="deck-track"
            animate={prefersReducedMotion ? { x: 0 } : { x: ['0%', '-50%'] }}
            transition={prefersReducedMotion ? { duration: 0 } : { duration: 40, repeat: Infinity, ease: 'linear' }}
          >
            {marqueeServices.map((service, index) => {
              const isActive = activeCard === index;

              return (
                <motion.div
                  key={`${service.id}-${index}`}
                  className={`deck-card ${isActive ? 'active' : ''}`}
                  onClick={() => onServiceClick(service.id)}
                  onHoverStart={() => setActiveCard(index)}
                  layout
                  transition={{ type: 'spring', stiffness: 200, damping: 25 }}
                >
                  <div
                    className="card-bg"
                    style={{ backgroundImage: `url(${service.imageUrl})` }}
                  />
                  <div className="card-overlay" />

                  <div className="card-content">
                    <motion.div layout className="card-header">
                      <div className="card-icon-wrapper">
                        <i className={service.icon}></i>
                      </div>
                      <motion.h3 layout="position" className="card-title">
                        {renderCardTitle(service.title)}
                      </motion.h3>
                    </motion.div>

                    <AnimatePresence>
                      {isActive && (
                        <motion.p
                          className="card-desc"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10, transition: { duration: 0.2 } }}
                          transition={{ delay: 0.2, duration: 0.4 }}
                        >
                          {service.description}
                        </motion.p>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
